import type {
  ConfirmPlacementAction,
  DrawDraftOptionsAction,
  KingsCrownAction,
  KingsCrownState,
  PickDraftTokenAction,
  ValidationResult,
} from "../types";
import {
  KINGS_CROWN_MAX_HAND_SIZE,
  KINGS_CROWN_TABLE_SLOT_COUNT,
} from "../types";
import { fail, getPlayer, isLegalPlacement, ok, parseCellId } from "../utils";

function validateKnownCurrentPlayer(
  state: KingsCrownState,
  playerId: string,
): ValidationResult {
  if (!getPlayer(state, playerId)) {
    return fail("ERR_UNKNOWN_PLAYER", { playerId });
  }
  if (state.currentPlayerId !== playerId) {
    return fail("ERR_NOT_CURRENT_PLAYER", {
      currentPlayerId: state.currentPlayerId,
      playerId,
    });
  }
  return ok;
}

export function validateDrawDraftOptions(
  state: KingsCrownState,
  action: DrawDraftOptionsAction,
): ValidationResult {
  const turn = validateKnownCurrentPlayer(state, action.playerId);
  if (!turn.valid) return turn;
  if (state.phase !== "DRAFT_DRAW") {
    return fail("ERR_WRONG_PHASE", { phase: state.phase });
  }
  const futureSlots = state.table.filter((slot) => slot.state === "future");
  if (futureSlots.length >= 2 && state.bag.length >= 2) {
    return ok;
  }

  const faceDownSlots = state.table.filter(
    (slot) => slot.state === "occupied" && slot.face === "down",
  );
  if (faceDownSlots.length >= 2) {
    return ok;
  }

  return fail("ERR_NO_DRAFT_SOURCE", {
    bagCount: state.bag.length,
    futureSlotCount: futureSlots.length,
    faceDownSlotCount: faceDownSlots.length,
  });
}

export function validatePickDraftToken(
  state: KingsCrownState,
  action: PickDraftTokenAction,
): ValidationResult {
  const turn = validateKnownCurrentPlayer(state, action.playerId);
  if (!turn.valid) return turn;
  if (state.phase !== "DRAFT_PICK") {
    return fail("ERR_WRONG_PHASE", { phase: state.phase });
  }
  const player = getPlayer(state, action.playerId);
  if (!player || player.hand.length >= KINGS_CROWN_MAX_HAND_SIZE) {
    return fail("ERR_HAND_FULL", { playerId: action.playerId });
  }
  if (action.source === "bag") {
    return state.bag.length > 0 ? ok : fail("ERR_EMPTY_BAG");
  }
  if (!action.slotId) {
    return fail("ERR_SLOT_REQUIRED");
  }
  const slot = state.table.find(
    (candidate) => candidate.slotId === action.slotId,
  );
  if (!slot || slot.state !== "occupied" || !slot.chip) {
    return fail("ERR_INVALID_TABLE_SLOT", { slotId: action.slotId });
  }
  return ok;
}

export function validateConfirmPlacement(
  state: KingsCrownState,
  action: ConfirmPlacementAction,
): ValidationResult {
  const turn = validateKnownCurrentPlayer(state, action.playerId);
  if (!turn.valid) return turn;
  if (state.phase !== "PLACE") {
    return fail("ERR_WRONG_PHASE", { phase: state.phase });
  }
  const player = getPlayer(state, action.playerId);
  const chip = player?.hand.find(
    (candidate) => candidate.chipId === action.chipId,
  );
  if (!player || !chip) {
    return fail("ERR_CHIP_NOT_IN_HAND", { chipId: action.chipId });
  }
  const parsed = parseCellId(action.cellId);
  if (!parsed) {
    return fail("ERR_INVALID_CELL", { cellId: action.cellId });
  }
  const cell = state.board[parsed.row]?.[parsed.col];
  if (cell === "CENTER") {
    return fail("ERR_CENTER_CELL", { cellId: action.cellId });
  }
  if (cell !== null) {
    return fail("ERR_CELL_OCCUPIED", { cellId: action.cellId });
  }
  if (!isLegalPlacement(state, chip, action.cellId)) {
    return fail("ERR_ILLEGAL_ADJACENCY", {
      cellId: action.cellId,
      chipId: chip.chipId,
    });
  }
  return ok;
}

export function validateAction(
  state: KingsCrownState,
  action: KingsCrownAction,
): ValidationResult {
  if (state.completedActionIds.includes(action.actionId)) {
    return ok;
  }
  if (state.phase === "ENDED" && action.type !== "SyncViewerState") {
    return fail("ERR_GAME_ENDED", { winnerId: state.winnerId });
  }
  switch (action.type) {
    case "DrawDraftOptions":
      return validateDrawDraftOptions(state, action);
    case "PickDraftToken":
      return validatePickDraftToken(state, action);
    case "ConfirmPlacement":
      return validateConfirmPlacement(state, action);
    case "SyncViewerState":
      return getPlayer(state, action.playerId)
        ? ok
        : fail("ERR_UNKNOWN_PLAYER", { playerId: action.playerId });
  }
}

export function assertTableSlotContract(
  state: KingsCrownState,
): ValidationResult {
  if (state.table.length !== KINGS_CROWN_TABLE_SLOT_COUNT) {
    return fail("ERR_TABLE_SLOT_COUNT", { count: state.table.length });
  }
  return ok;
}
